import React, { memo } from 'react';
import type { RichTextEntryType } from 'tsconfig/types.d';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { fontCorrectionInRichText } from '../../../utils/fontCorrectionInRichText';
import RichText from '../../RichText/RichText';
import { contentToSanitize, parseHtmlContent } from '../../../utils/richTextHelper';

export type CmsRichTextProps = {
  contentItem: RichTextEntryType;
  dlClickEventHandler?: (data: string) => void;
  onLinkClick?: (details: CmsRichTextLinkClickDetails) => void;
};

export type CmsRichTextLinkClickDetails = {
  clickText: string;
  clickDescription: string;
  destinationUrl: string;
};

export const CmsRichText = memo((props: CmsRichTextProps) => {
  const { contentItem, dlClickEventHandler, onLinkClick } = props;
  const { backgroundColour, content } = contentItem?.fields || {};
  const theme = useTheme();
  const widgetTheme = theme.widgets.CmsRichText || {};
  const Wrapper = styled.section`
    ${backgroundColour && ` background-color: ${backgroundColour}`}
    ${widgetTheme.wrapper}
  `;

  const handleClickEvent = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');

    if (!anchor) {
      return;
    }

    // Clone the anchor so we don't modify the original
    const clone = target.cloneNode(true) as Element;

    // Remove all elements that are hidden with display: none
    for (const el of clone.querySelectorAll('*')) {
      const inlineStyle = (el as HTMLElement).style.display;
      if (inlineStyle === 'none') {
        el.remove();
      }
    }

    const visibleTargetText = clone.textContent?.trim() || '';

    const visibleLink = anchor.cloneNode(true) as HTMLElement;
    for (const el of visibleLink.querySelectorAll('*')) {
      if ((el as HTMLElement).style.display === 'none') {
        el.remove();
      }
    }
    const clickText = (visibleLink.textContent || '').replace(/\s+/g, ' ').trim();
    if (!clickText) {
      return;
    }

    const messageClone = e.currentTarget.cloneNode(true) as HTMLElement;
    for (const el of messageClone.querySelectorAll('*')) {
      if ((el as HTMLElement).style.display === 'none') {
        el.remove();
      }
    }
    const clickDescription = (messageClone.innerText || messageClone.textContent || '').replace(/\s+/g, ' ').trim();

    // Try to get the href from clicked anchor element
    const destinationUrl = anchor.getAttribute('href')?.trim() || '';
    if (visibleTargetText) {
      dlClickEventHandler?.(`${visibleTargetText}:headline:${visibleTargetText}:${destinationUrl}`);
    }
    onLinkClick?.({ clickText, clickDescription, destinationUrl: anchor.href });
  };

  // Compute content synchronously — works in SSG, SSR, and CSR
  const modifiedContent = parseHtmlContent(fontCorrectionInRichText(contentToSanitize(contentItem), theme.fonts));

  //see https://www.contentful.com/developers/docs/concepts/rich-text/
  return (
    <Wrapper
      data-test="contentful-textbox"
      className={`richtext-wrapper ${backgroundColour ? 'richtext-has-bg' : ''}`}
      onClick={handleClickEvent}
    >
      {typeof content === 'string' && <RichText sysId={contentItem.sys.id} content={modifiedContent} />}
    </Wrapper>
  );
});
